import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Shield, Lock, Mail, Phone, MapPin, MessageCircle, ChevronRight, FileText, HelpCircle, Users, Award, Clock, RefreshCw } from 'lucide-react';

const PAGES = {
  terms: {
    title: 'Terms & Conditions',
    icon: FileText,
    sections: [
      { heading: 'Acceptance of Terms', body: 'By accessing and using the Lunora\'s platform, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.' },
      { heading: 'Services', body: 'Lunora\'s provides a custom fashion marketplace connecting customers with verified tailors. We offer AI-powered body measurements, design customization, and doorstep delivery of custom-tailored garments.' },
      { heading: 'Orders & Payments', body: 'All orders are subject to acceptance by our tailor partners. Prices displayed include base garment cost and selected customizations. Payment is processed securely through our payment partners. COD is available on select orders.' },
      { heading: 'Cancellation Policy', body: 'Orders can be cancelled before stitching begins for a full refund. Orders in stitching phase are eligible for 50% refund. Once a garment is ready or shipped, cancellation is not available.' },
      { heading: 'Quality Guarantee', body: 'We offer free alterations within 7 days of delivery. If the garment does not match specifications, we will remake it at no additional cost.' },
      { heading: 'Intellectual Property', body: 'All content, designs, and branding on Lunora\'s platform are proprietary. Users may not reproduce, distribute, or create derivative works without explicit written permission.' },
      { heading: 'Limitation of Liability', body: 'Lunora\'s acts as a marketplace connecting customers with tailors. While we ensure quality standards, the final craftsmanship is the responsibility of the assigned tailor partner.' },
    ]
  },
  privacy: {
    title: 'Privacy Policy',
    icon: Lock,
    sections: [
      { heading: 'Information We Collect', body: 'We collect personal information you provide (name, email, phone, address), body measurements, order history, and device information for analytics. Photos uploaded for AI sizing are processed locally and not stored on our servers.' },
      { heading: 'How We Use Your Information', body: 'Your data is used to process orders, assign tailors, improve our AI sizing accuracy, send order updates, and personalize your experience. We do not sell your personal information to third parties.' },
      { heading: 'Data Security', body: 'We use industry-standard encryption (AES-256) for data at rest and TLS 1.3 for data in transit. Passwords are hashed using bcrypt. Payment information is processed by PCI-DSS compliant partners.' },
      { heading: 'Your Rights', body: 'You can request access to, correction of, or deletion of your personal data at any time. Contact us at privacy@lunoras.com. We will respond within 30 business days.' },
      { heading: 'Cookies & Analytics', body: 'We use essential cookies for authentication and analytics cookies to improve our service. You can manage cookie preferences through your browser settings.' },
      { heading: 'Data Retention', body: 'Order data is retained for 3 years for business and legal purposes. Account data is deleted within 30 days of account closure request.' },
    ]
  },
  contact: {
    title: 'Contact Us',
    icon: Mail,
    isContact: true,
    contacts: [
      { icon: Mail, label: 'Email', value: 'hello@lunoras.com', href: 'mailto:hello@lunoras.com' },
      { icon: Phone, label: 'Phone', value: '+91 98765 43210', href: 'tel:+919876543210' },
      { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with us', href: 'https://wa.me/919876543210' },
      { icon: MapPin, label: 'Address', value: 'Lunora\'s HQ, 42 Fashion Street, Jaipur, Rajasthan 302001' },
      { icon: Clock, label: 'Hours', value: 'Mon-Sat: 9 AM - 8 PM IST' },
    ]
  },
  faq: {
    title: 'FAQ',
    icon: HelpCircle,
    faqs: [
      { q: 'How does AI body sizing work?', a: 'Upload a full-body photo, and our AI uses MediaPipe technology to detect 33 body landmarks. Combined with your height input, it calculates chest, waist, hip, shoulder, and length measurements with 95% accuracy.' },
      { q: 'How long does delivery take?', a: 'Standard delivery: 7-10 days. Urgent: 3-5 days (+₹499). Flexible: 10-15 days (save ₹100). Custom garments require time for stitching and quality checks.' },
      { q: 'Can I cancel my order?', a: 'Yes. Full refund before stitching starts. 50% refund during stitching. No refund once the garment is ready or shipped.' },
      { q: 'What if the fit is not right?', a: 'We offer free alterations within 7 days of delivery. If the garment doesn\'t match your specifications, we\'ll remake it at no extra cost.' },
      { q: 'How are tailors verified?', a: 'Every tailor undergoes a 3-step verification: portfolio review, sample stitching test, and customer feedback period. Only top-rated tailors remain on our platform.' },
      { q: 'What payment methods do you accept?', a: 'We accept UPI (GPay, PhonePe, Paytm), Credit/Debit Cards, Net Banking, and Cash on Delivery.' },
      { q: 'Can I customize the design?', a: 'Absolutely. Choose from 8 customization categories: Work, Fabric, Sleeves, Front Design, Back Design, Neck Style, Color, and Size. Each option has real-time pricing.' },
      { q: 'How do I track my order?', a: 'Go to My Orders from the menu. Each order shows a live 6-stage tracking timeline: Placed > Tailor Assigned > Stitching > Quality Check > Out for Delivery > Delivered.' },
    ]
  },
  help: {
    title: 'Help Center',
    icon: HelpCircle,
    isHelp: true,
    topics: [
      { title: 'Getting Started', desc: 'How to create account, browse, and order', path: '/info/faq' },
      { title: 'Orders & Tracking', desc: 'Track, modify, or cancel your orders', path: '/my-orders' },
      { title: 'Payments & Refunds', desc: 'Payment methods, refund status, wallet', path: '/info/faq' },
      { title: 'Size & Fit', desc: 'AI sizing, manual measurements, alterations', path: '/info/faq' },
      { title: 'Tailor Partners', desc: 'Become a tailor, dashboard help', path: '/tailor-login' },
      { title: 'Contact Support', desc: 'Email, phone, WhatsApp', path: '/info/contact' },
    ]
  },
  about: {
    title: 'About Lunora\'s',
    icon: Award,
    isAbout: true,
    stats: [
      { value: '25,000+', label: 'Happy Customers' },
      { value: '500+', label: 'Verified Tailors' },
      { value: '50,000+', label: 'Orders Delivered' },
      { value: '95%', label: 'AI Sizing Accuracy' },
    ],
    story: 'Lunora\'s was born from a simple problem: why does online fashion still guess your size? We combined advanced AI body measurement technology with India\'s finest tailoring tradition to create a platform where every garment is made for your body. Our mission is to make custom-tailored fashion accessible, affordable, and delightful for everyone.'
  },
};

const InfoPage = () => {
  const navigate = useNavigate();
  const { page } = useParams();
  const data = PAGES[page];

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
        <div className="text-center">
          <p className="text-[14px]" style={{ color: '#595959' }}>Page not found</p>
          <button onClick={() => navigate('/landing')} className="mt-3 text-[12px]" style={{ color: '#D4AF37' }}>Go Home</button>
        </div>
      </div>
    );
  }

  const Icon = data.icon;

  return (
    <div data-testid={`info-${page}`} className="min-h-screen pb-8" style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>
      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center gap-2 px-3 py-3 border-b bg-white" style={{ borderColor: '#E5E5E5' }}>
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-1">
          <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{data.title}</h1>
      </div>

      <div className="px-4 py-5">
        {/* Hero */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
            <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{data.title}</h2>
        </div>

        {/* Terms / Privacy Sections */}
        {data.sections && (
          <div className="space-y-4">
            {data.sections.map((s, i) => (
              <div key={i} className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
                <h3 className="text-[13px] font-medium mb-2" style={{ color: '#0F0F0F' }}>{s.heading}</h3>
                <p className="text-[12px] leading-relaxed" style={{ color: '#595959' }}>{s.body}</p>
              </div>
            ))}
            <p className="text-[10px] text-center" style={{ color: '#BFBFBF' }}>Last updated: February 2026</p>
          </div>
        )}

        {/* Contact */}
        {data.isContact && (
          <div className="space-y-2">
            {data.contacts.map((c, i) => {
              const CI = c.icon;
              return c.href ? (
                <a key={i} href={c.href} className="flex items-center gap-3 bg-white border p-4" style={{ borderColor: '#E5E5E5', textDecoration: 'none' }}>
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(212,175,55,0.08)' }}>
                    <CI className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[11px]" style={{ color: '#595959' }}>{c.label}</p>
                    <p className="text-[13px]" style={{ color: '#0F0F0F' }}>{c.value}</p>
                  </div>
                  <ChevronRight className="w-4 h-4" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
                </a>
              ) : (
                <div key={i} className="flex items-center gap-3 bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(212,175,55,0.08)' }}>
                    <CI className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-[11px]" style={{ color: '#595959' }}>{c.label}</p>
                    <p className="text-[13px]" style={{ color: '#0F0F0F' }}>{c.value}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* FAQ */}
        {data.faqs && (
          <div className="space-y-2">
            {data.faqs.map((f, i) => (
              <details key={i} className="bg-white border group" style={{ borderColor: '#E5E5E5' }}>
                <summary className="px-4 py-3 cursor-pointer flex items-center justify-between text-[13px] list-none" style={{ color: '#0F0F0F' }}>
                  {f.q}
                  <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" style={{ color: '#595959' }} strokeWidth={1.5} />
                </summary>
                <div className="px-4 pb-3">
                  <p className="text-[12px] leading-relaxed" style={{ color: '#595959' }}>{f.a}</p>
                </div>
              </details>
            ))}
          </div>
        )}

        {/* Help Center */}
        {data.isHelp && (
          <div className="space-y-2">
            {data.topics.map((t, i) => (
              <button key={i} onClick={() => navigate(t.path)}
                className="w-full flex items-center gap-3 bg-white border p-4 text-left" style={{ borderColor: '#E5E5E5' }}>
                <div className="flex-1">
                  <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>{t.title}</p>
                  <p className="text-[11px]" style={{ color: '#595959' }}>{t.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: '#BFBFBF' }} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )}

        {/* About */}
        {data.isAbout && (
          <>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {data.stats.map((s, i) => (
                <div key={i} className="bg-white border p-4 text-center" style={{ borderColor: '#E5E5E5' }}>
                  <p className="text-xl font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{s.value}</p>
                  <p className="text-[10px]" style={{ color: '#595959' }}>{s.label}</p>
                </div>
              ))}
            </div>
            <div className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
              <h3 className="text-[13px] font-medium mb-2" style={{ color: '#0F0F0F' }}>Our Story</h3>
              <p className="text-[12px] leading-relaxed" style={{ color: '#595959' }}>{data.story}</p>
            </div>
            <div className="mt-4 bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
              <h3 className="text-[13px] font-medium mb-3" style={{ color: '#0F0F0F' }}>Why Lunora's?</h3>
              {[
                { icon: Shield, text: 'AI-powered body sizing with 95% accuracy' },
                { icon: Users, text: '500+ verified master tailors across India' },
                { icon: Award, text: 'Free alterations within 7 days' },
                { icon: RefreshCw, text: 'Easy cancellation and refund process' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5 py-2">
                  <item.icon className="w-4 h-4 flex-shrink-0" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
                  <span className="text-[12px]" style={{ color: '#595959' }}>{item.text}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ChevronDown needs separate import for FAQ
const ChevronDown = ({ className, style, strokeWidth }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} style={style}><path d="m6 9 6 6 6-6"/></svg>
);

export default InfoPage;
